// src/pages/AdminPanel/LibraryAccess.jsx

import React, { useCallback, useEffect, useMemo, useState } from "react";
import SectionCard from "components/SectionCard";
import { supabase } from "services/supabaseClient";
import { useProfile } from "context/ProfileContext";
import {
  ChevronRight,
  ChevronLeft,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
} from "lucide-react";

/**
 * Library Access (Entitlements + Apply publishing)
 * -----------------------------------------------------------------------------
 * Platform admin grants org-level entitlements to DebtStudio library issuers,
 * then "Apply" reconciles publishing/access for the selected org.
 *
 * Entitlements:
 *  - ds_lib_org_entitlements (org_id, issuer_id, created_by)
 *
 * Apply RPC:
 *  - ds_lib_apply_org_entitlements(p_org_id uuid)
 *
 * Publish status RPC:
 *  - ds_lib_get_org_publish_status(p_org_id uuid)
 *    returns: issuer_id, total_issues, published_issues
 */

function canBool(v) {
  return !!v;
}

function resolveRoles(profile) {
  const isPlatformAdmin =
    canBool(profile?.is_admin) || canBool(profile?.app_metadata?.is_admin);

  const isOrgAdmin =
    canBool(profile?.is_org_admin) ||
    canBool(profile?.app_metadata?.is_org_admin);

  return { isPlatformAdmin, isOrgAdmin };
}

/* -------------------------------------------------------------------------- */
/*  Data access                                                               */
/* -------------------------------------------------------------------------- */

async function fetchAllOrgs() {
  const { data, error } = await supabase
    .from("orgs")
    .select("id, name")
    .order("name");
  if (error) throw error;
  return data ?? [];
}

/**
 * Authorized issuers for an org.
 * Two-step fetch to avoid schema-cache relationship issues.
 */
async function fetchEntitledIssuers({ orgId }) {
  if (!orgId) return [];

  const { data: ent, error: entErr } = await supabase
    .from("ds_lib_org_entitlements")
    .select("id, issuer_id")
    .eq("org_id", orgId)
    .not("issuer_id", "is", null);

  if (entErr) throw entErr;

  const entRows = ent ?? [];
  const issuerIds = Array.from(
    new Set(entRows.map((r) => r.issuer_id).filter(Boolean))
  );

  // issuers table uses legal_name, not name
  let issuerLabelById = new Map();
  if (issuerIds.length) {
    const { data: iss, error: issErr } = await supabase
      .from("issuers")
      .select("id, legal_name")
      .in("id", issuerIds);

    if (issErr) throw issErr;

    issuerLabelById = new Map((iss ?? []).map((x) => [x.id, x.legal_name]));
  }

  const rows = entRows.map((r) => ({
    entitlement_id: r.id,
    issuer_id: r.issuer_id,
    legal_name: issuerLabelById.get(r.issuer_id) ?? "(unknown issuer)",
  }));

  rows.sort((a, b) => String(a.legal_name).localeCompare(String(b.legal_name)));
  return rows;
}

/**
 * Library issuers derived from ds_deals_lib (final-only library).
 * We use issuer_name_raw for display, because ds_deals_lib references issuers(id)
 * and we want a stable label even if issuers.legal_name differs.
 */
async function fetchLibraryIssuers() {
  const { data, error } = await supabase
    .from("ds_deals_lib")
    .select("issuer_id, issuer_name_raw")
    .not("issuer_id", "is", null)
    .limit(2000);

  if (error) throw error;

  const map = new Map();
  for (const r of data ?? []) {
    const id = r.issuer_id;
    if (!id) continue;
    if (!map.has(id)) {
      map.set(id, {
        issuer_id: id,
        label: r.issuer_name_raw ?? String(id),
      });
    }
  }

  const issuers = Array.from(map.values());
  issuers.sort((a, b) => String(a.label).localeCompare(String(b.label)));
  return issuers;
}

async function fetchPublishStatusMap({ orgId }) {
  if (!orgId) return new Map();

  const { data, error } = await supabase.rpc("ds_lib_get_org_publish_status", {
    p_org_id: orgId,
  });

  if (error) throw error;

  const map = new Map();
  for (const row of data ?? []) {
    if (!row?.issuer_id) continue;
    map.set(row.issuer_id, {
      total: Number(row.total_issues ?? 0),
      published: Number(row.published_issues ?? 0),
    });
  }
  return map;
}

async function authorizeIssuersForOrg({ orgId, issuerIds, createdBy }) {
  if (!orgId || !issuerIds?.length) return;

  // created_by is a FK to profiles(id), so we only set it when we have it.
  const created_by = createdBy ?? null;

  const payload = issuerIds.map((issuerId) => ({
    org_id: orgId,
    issuer_id: issuerId,
    created_by,
  }));

  const { error } = await supabase
    .from("ds_lib_org_entitlements")
    .insert(payload);

  if (error) throw error;
}

async function revokeEntitlements({ entitlementIds }) {
  if (!entitlementIds?.length) return;

  const { error } = await supabase
    .from("ds_lib_org_entitlements")
    .delete()
    .in("id", entitlementIds);

  if (error) throw error;
}

async function applyPublishingForOrg({ orgId }) {
  if (!orgId) return { ok: false, reason: "missing_org" };

  const { data, error } = await supabase.rpc("ds_lib_apply_org_entitlements", {
    p_org_id: orgId,
  });

  if (error) throw error;
  return data ?? { ok: true };
}

/* -------------------------------------------------------------------------- */
/*  Shared picklist pane (PortfolioComposition-style)                          */
/* -------------------------------------------------------------------------- */

function ListPane({
  title,
  subtitle,
  items,
  selectedIds,
  onToggle,
  onToggleAll,
  emptyText,
  disabled,

  sortField,
  sortAsc,
  setSortField,
  setSortAsc,

  showSearch = false,
  searchValue = "",
  onSearchChange = () => {},
  searchPlaceholder = "Search…",
}) {
  const allIds = useMemo(() => items.map((x) => x.id), [items]);

  const allSelected = useMemo(() => {
    if (!allIds.length) return false;
    return allIds.every((id) => selectedIds.has(id));
  }, [allIds, selectedIds]);

  const someSelected = useMemo(() => {
    if (!allIds.length) return false;
    return allIds.some((id) => selectedIds.has(id));
  }, [allIds, selectedIds]);

  const renderSortIcon = (active) => {
    if (!active) return <ArrowUpDown className="w-3.5 h-3.5" />;
    return sortAsc ? (
      <ArrowUp className="w-3.5 h-3.5 text-app" />
    ) : (
      <ArrowDown className="w-3.5 h-3.5 text-app" />
    );
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortAsc((v) => !v);
    } else {
      setSortField(field);
      setSortAsc(true);
    }
  };

  const badgeCls = (tone) => {
    // We keep text as text-app for theme consistency, but tint the surface/border.
    // tone: "ok" (green), "warn" (amber), "muted"
    if (tone === "ok") {
      return "border-emerald-300/50 bg-emerald-500/10 text-app";
    }
    if (tone === "warn") {
      return "border-amber-300/50 bg-amber-500/10 text-app";
    }
    return "border-border bg-app text-muted";
  };

  return (
    <div className="min-w-0">
      <div className="rounded-xl border border-border bg-app overflow-hidden min-w-0">
        <div className="px-3 py-2 border-b border-border">
          <div className="grid grid-cols-[1fr_auto] gap-3 items-center min-h-[52px]">
            <div className="min-w-0">
              <div className="text-app font-semibold leading-5">{title}</div>
              {subtitle ? (
                <div className="text-muted text-sm leading-5">{subtitle}</div>
              ) : (
                <div className="text-muted text-sm leading-5 opacity-0">.</div>
              )}
            </div>

            {showSearch ? (
              <input
                type="text"
                value={searchValue}
                onChange={(e) => onSearchChange(e.target.value)}
                placeholder={searchPlaceholder}
                className="h-8 w-56 max-w-full rounded-md border border-border bg-app px-2 text-sm text-app placeholder:text-muted"
              />
            ) : (
              <div className="w-56" />
            )}
          </div>
        </div>

        <div className="grid grid-cols-[auto_1fr] gap-2 px-3 py-2 border-b border-border text-sm font-semibold select-none">
          <input
            type="checkbox"
            checked={allSelected}
            ref={(el) => {
              if (el) el.indeterminate = !allSelected && someSelected;
            }}
            onChange={() => onToggleAll?.(!allSelected)}
            disabled={disabled || items.length === 0}
            aria-label="Select all"
          />

          <div
            className="flex items-center gap-1 cursor-pointer"
            onClick={() => handleSort("label")}
          >
            Issuer
            {renderSortIcon(sortField === "label")}
          </div>
        </div>

        <div className="max-h-[420px] overflow-auto">
          {items.length === 0 ? (
            <div className="p-3 text-muted italic">{emptyText}</div>
          ) : (
            items.map((x) => {
              const checked = selectedIds.has(x.id);

              return (
                <label
                  key={x.id}
                  className="grid grid-cols-[auto_1fr] gap-2 px-3 py-2 cursor-pointer hover:bg-white/10 min-w-0"
                >
                  <input
                    type="checkbox"
                    className="mt-1 shrink-0"
                    checked={checked}
                    onChange={() => onToggle?.(x.id)}
                    disabled={disabled}
                  />

                  <div className="min-w-0">
                    <div className="flex items-start justify-between gap-2 min-w-0">
                      <div
                        className="text-app text-xs whitespace-normal break-words min-w-0"
                        title={x.label}
                      >
                        {x.label}
                      </div>

                      {x.badge ? (
                        <span
                          className={[
                            "shrink-0 rounded-full px-2 py-0.5 text-[11px] border",
                            badgeCls(x.badgeTone),
                          ].join(" ")}
                          title={x.badge}
                        >
                          {x.badge}
                        </span>
                      ) : null}
                    </div>

                    {x.sub ? (
                      <div className="text-muted text-xs mt-0.5">{x.sub}</div>
                    ) : null}
                  </div>
                </label>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Main Component                                                             */
/* -------------------------------------------------------------------------- */

export default function LibraryAccess() {
  const { profile } = useProfile();
  const { isPlatformAdmin } = useMemo(() => resolveRoles(profile), [profile]);

  const [orgs, setOrgs] = useState([]);
  const [actingOrgId, setActingOrgId] = useState("");

  const [libraryIssuers, setLibraryIssuers] = useState([]);
  const [entitledIssuers, setEntitledIssuers] = useState([]);
  const [publishStatusMap, setPublishStatusMap] = useState(new Map());

  const [selectedAvailableIds, setSelectedAvailableIds] = useState(new Set());
  const [selectedEntitlementIds, setSelectedEntitlementIds] = useState(new Set());

  const [availableSearch, setAvailableSearch] = useState("");
  const [entitledSearch, setEntitledSearch] = useState("");

  const [availSortField, setAvailSortField] = useState("label");
  const [availSortAsc, setAvailSortAsc] = useState(true);
  const [entSortField, setEntSortField] = useState("label");
  const [entSortAsc, setEntSortAsc] = useState(true);

  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [applyResult, setApplyResult] = useState(null);

  useEffect(() => {
    if (!isPlatformAdmin) {
      setLoading(false);
      return;
    }

    (async () => {
      try {
        setLoading(true);
        setError("");
        const o = await fetchAllOrgs();
        setOrgs(o);
      } catch (e) {
        setError(e?.message ?? String(e));
      } finally {
        setLoading(false);
      }
    })();
  }, [isPlatformAdmin]);

  const actingOrgName =
    orgs.find((o) => o.id === actingOrgId)?.name ??
    (actingOrgId ? "(unknown org)" : "");

  const entitledIssuerIdSet = useMemo(() => {
    return new Set(entitledIssuers.map((x) => x.issuer_id));
  }, [entitledIssuers]);

  const fetchAll = useCallback(async () => {
    if (!actingOrgId) {
      setLibraryIssuers([]);
      setEntitledIssuers([]);
      setPublishStatusMap(new Map());
      setSelectedAvailableIds(new Set());
      setSelectedEntitlementIds(new Set());
      setApplyResult(null);
      return;
    }

    setBusy(true);
    setError("");

    try {
      const [lib, ent, pubMap] = await Promise.all([
        fetchLibraryIssuers(),
        fetchEntitledIssuers({ orgId: actingOrgId }),
        fetchPublishStatusMap({ orgId: actingOrgId }),
      ]);

      setLibraryIssuers(Array.isArray(lib) ? lib : []);
      setEntitledIssuers(Array.isArray(ent) ? ent : []);
      setPublishStatusMap(pubMap instanceof Map ? pubMap : new Map());

      setSelectedAvailableIds(new Set());
      setSelectedEntitlementIds(new Set());
    } catch (e) {
      setError(e?.message ?? "Failed to load library access data.");
      setLibraryIssuers([]);
      setEntitledIssuers([]);
      setPublishStatusMap(new Map());
      setSelectedAvailableIds(new Set());
      setSelectedEntitlementIds(new Set());
    } finally {
      setBusy(false);
    }
  }, [actingOrgId]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const availableIssuerItems = useMemo(() => {
    const q = availableSearch.trim().toLowerCase();

    const rows = libraryIssuers
      .filter((x) => x?.issuer_id && !entitledIssuerIdSet.has(x.issuer_id))
      .map((x) => ({
        id: x.issuer_id,
        label: x.label || String(x.issuer_id),
        sub: String(x.issuer_id),
      }))
      .filter((x) => {
        if (!q) return true;
        return (
          (x.label || "").toLowerCase().includes(q) ||
          (x.sub || "").toLowerCase().includes(q)
        );
      });

    return [...rows].sort((a, b) => {
      const aVal = (a[availSortField] || "").toLowerCase();
      const bVal = (b[availSortField] || "").toLowerCase();
      if (aVal < bVal) return availSortAsc ? -1 : 1;
      if (aVal > bVal) return availSortAsc ? 1 : -1;
      return 0;
    });
  }, [
    libraryIssuers,
    entitledIssuerIdSet,
    availableSearch,
    availSortField,
    availSortAsc,
  ]);

  const entitledIssuerItems = useMemo(() => {
    const q = entitledSearch.trim().toLowerCase();

    const rows = entitledIssuers
      .map((x) => {
        const status = publishStatusMap.get(x.issuer_id);
        const total = status?.total ?? 0;
        const published = status?.published ?? 0;

        let badge = null;
        let badgeTone = "muted";
        if (total > 0) {
          if (published >= total) {
            badge = `Published (${published}/${total})`;
            badgeTone = "ok";
          } else if (published > 0) {
            badge = `Partial (${published}/${total})`;
            badgeTone = "warn";
          } else {
            badge = `Not applied (0/${total})`;
            badgeTone = "muted";
          }
        }

        return {
          id: x.entitlement_id,
          label: x.legal_name || "(unknown issuer)",
          sub: String(x.issuer_id),
          issuer_id: x.issuer_id,
          badge,
          badgeTone,
        };
      })
      .filter((x) => {
        if (!q) return true;
        return (
          (x.label || "").toLowerCase().includes(q) ||
          (x.sub || "").toLowerCase().includes(q) ||
          (x.badge || "").toLowerCase().includes(q)
        );
      });

    return [...rows].sort((a, b) => {
      const aVal = (a[entSortField] || "").toLowerCase();
      const bVal = (b[entSortField] || "").toLowerCase();
      if (aVal < bVal) return entSortAsc ? -1 : 1;
      if (aVal > bVal) return entSortAsc ? 1 : -1;
      return 0;
    });
  }, [entitledIssuers, publishStatusMap, entitledSearch, entSortField, entSortAsc]);

  const toggleAvailable = useCallback((issuerId) => {
    setSelectedAvailableIds((prev) => {
      const next = new Set(prev);
      next.has(issuerId) ? next.delete(issuerId) : next.add(issuerId);
      return next;
    });
    setSelectedEntitlementIds(new Set());
  }, []);

  const toggleEntitled = useCallback((entitlementId) => {
    setSelectedEntitlementIds((prev) => {
      const next = new Set(prev);
      next.has(entitlementId) ? next.delete(entitlementId) : next.add(entitlementId);
      return next;
    });
    setSelectedAvailableIds(new Set());
  }, []);

  const toggleAllAvailable = useCallback(
    (select) => {
      setSelectedAvailableIds(
        select ? new Set(availableIssuerItems.map((x) => x.id)) : new Set()
      );
      setSelectedEntitlementIds(new Set());
    },
    [availableIssuerItems]
  );

  const toggleAllEntitled = useCallback(
    (select) => {
      setSelectedEntitlementIds(
        select ? new Set(entitledIssuerItems.map((x) => x.id)) : new Set()
      );
      setSelectedAvailableIds(new Set());
    },
    [entitledIssuerItems]
  );

  const handleAuthorize = useCallback(async () => {
    if (!actingOrgId) return;
    if (selectedAvailableIds.size === 0) return;

    const createdBy = profile?.id;
    if (!createdBy) {
      setError("Missing profile.id, cannot set created_by for entitlements.");
      return;
    }

    setBusy(true);
    setError("");
    setApplyResult(null);

    try {
      await authorizeIssuersForOrg({
        orgId: actingOrgId,
        issuerIds: Array.from(selectedAvailableIds),
        createdBy,
      });

      await fetchAll();
    } catch (e) {
      setError(e?.message ?? "Failed to authorize issuers.");
    } finally {
      setBusy(false);
    }
  }, [actingOrgId, selectedAvailableIds, fetchAll, profile]);

  const handleRevoke = useCallback(async () => {
    if (!actingOrgId) return;
    if (selectedEntitlementIds.size === 0) return;

    setBusy(true);
    setError("");
    setApplyResult(null);

    try {
      await revokeEntitlements({
        entitlementIds: Array.from(selectedEntitlementIds),
      });

      await fetchAll();
    } catch (e) {
      setError(e?.message ?? "Failed to revoke issuer entitlements.");
    } finally {
      setBusy(false);
    }
  }, [actingOrgId, selectedEntitlementIds, fetchAll]);

  const handleApply = useCallback(async () => {
    if (!actingOrgId) return;

    setBusy(true);
    setError("");
    setApplyResult(null);

    try {
      const res = await applyPublishingForOrg({ orgId: actingOrgId });
      setApplyResult(res);

      await fetchAll();
    } catch (e) {
      setError(e?.message ?? "Failed to apply publishing for this org.");
    } finally {
      setBusy(false);
    }
  }, [actingOrgId, fetchAll]);

  if (loading) {
    return <div className="p-4 text-muted">Loading ds_lib access…</div>;
  }

  if (!isPlatformAdmin) {
    return (
      <SectionCard>
        <p className="text-sm text-muted">You do not have access to this section.</p>
      </SectionCard>
    );
  }

  const canAct = !!actingOrgId && !busy;
  const canAuthorize = canAct && selectedAvailableIds.size > 0;
  const canRevoke = canAct && selectedEntitlementIds.size > 0;
  const canApply = !!actingOrgId && !busy;

  return (
    <div className="space-y-6">
      {error && (
        <div className="rounded border border-red-200 bg-red-50 p-2 text-sm text-red-600">
          {error}
        </div>
      )}

      {applyResult?.ok ? (
        <div className="rounded border border-border bg-app p-2 text-sm text-app">
          <div className="font-semibold">Applied</div>
          <div className="text-muted">
            Published added: {applyResult.published_added ?? 0},{" "}
            Access added: {applyResult.access_added ?? 0},{" "}
            Access removed: {applyResult.access_removed ?? 0}
          </div>
        </div>
      ) : null}

      <SectionCard title="DebtStudio Entitlements">
        {/* Org picker + Apply button row */}
        <div className="max-w-xl">
          <label className="text-xs text-muted">Organization</label>
          <select
            className="mt-1 w-full rounded-xl border border-app bg-app px-3 py-2 text-sm text-app"
            value={actingOrgId}
            onChange={(e) => {
              setActingOrgId(e.target.value);
              setAvailableSearch("");
              setEntitledSearch("");
              setSelectedAvailableIds(new Set());
              setSelectedEntitlementIds(new Set());
              setApplyResult(null);
            }}
          >
            <option value="">Select organization…</option>
            {orgs.map((o) => (
              <option key={o.id} value={o.id}>
                {o.name}
              </option>
            ))}
          </select>
        </div>

        {actingOrgId ? (
          <div className="mt-4 flex items-center justify-between gap-3">
            <div className="text-xs text-muted">
              Selected: <span className="text-app">{actingOrgName}</span>
            </div>

            {/* Right-justified, standard primary button (not amber) */}
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleApply}
              disabled={!canApply}
              title="Apply publishing/access to match Authorized Issuers for this org"
            >
              Apply
            </button>
          </div>
        ) : null}

        {/* List panes */}
        {!actingOrgId ? null : (
          <div className="mt-4 grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] gap-4 items-start text-sm">
            <ListPane
              title="Available Library Issuers"
              subtitle={busy ? "Loading…" : `${availableIssuerItems.length} available`}
              items={availableIssuerItems}
              selectedIds={selectedAvailableIds}
              onToggle={toggleAvailable}
              onToggleAll={toggleAllAvailable}
              emptyText="No available issuers."
              disabled={!canAct}
              sortField={availSortField}
              sortAsc={availSortAsc}
              setSortField={setAvailSortField}
              setSortAsc={setAvailSortAsc}
              showSearch
              searchValue={availableSearch}
              onSearchChange={setAvailableSearch}
              searchPlaceholder="Search issuers…"
            />

            <div className="flex md:flex-col gap-3 justify-start items-center pt-28">
              <button
                type="button"
                className="btn btn-primary px-2"
                onClick={handleAuthorize}
                disabled={!canAuthorize}
                aria-label="Authorize selected issuers"
                title="Authorize selected issuers for this org (does not publish until Apply)"
              >
                <ChevronRight className="w-4 h-4" />
              </button>

              {/* Keep amber revoke button */}
              <button
                type="button"
                className="btn px-2 bg-amber-600/80 hover:bg-amber-600 text-white disabled:opacity-50"
                onClick={handleRevoke}
                disabled={!canRevoke}
                aria-label="Revoke selected issuers"
                title="Revoke selected issuer entitlements for this org (then Apply to reconcile access)"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
            </div>

            <ListPane
              title="Authorized Issuers"
              subtitle={busy ? "Loading…" : `${entitledIssuerItems.length} authorized`}
              items={entitledIssuerItems}
              selectedIds={selectedEntitlementIds}
              onToggle={toggleEntitled}
              onToggleAll={toggleAllEntitled}
              emptyText="No authorized issuers."
              disabled={!canAct}
              sortField={entSortField}
              sortAsc={entSortAsc}
              setSortField={setEntSortField}
              setSortAsc={setEntSortAsc}
              showSearch
              searchValue={entitledSearch}
              onSearchChange={setEntitledSearch}
              searchPlaceholder="Search authorized…"
            />
          </div>
        )}
      </SectionCard>
    </div>
  );
}
